/* eslint-disable no-unused-vars */
/* eslint-disable no-unused-expressions */
import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext } from 'react';
import ShowroomCBBService from 'trade-in/services/ShowroomCBBService/ShowroomCBBService.js';
import TradeInService from 'trade-in/services/TradeInService/TradeInService.js';
import TradeInModelNew from 'trade-in/model/TradeInModelNew.js';
import REGEX from 'trade-in/constants/regex.js';
import 'trade-in/components/InstantEstimateWrapper/Steps/ContactInformations/ContactInformations.scss';
import { isEmpty } from 'lodash';
import { LocaleService, EmailService, ConfigsService } from '@sm360/widget-commons-core-services';
import { ValidationService as VS } from '@sm360/widget-commons-forms';
import { AccountService, ProfileService } from '@sm360/widget-commons-general';
import { Vehicle } from '@sm360/widget-commons-vehicle';
import { Input, InputPhone, Checkbox, Card, CardSection } from '@sm360/ui-library';
import { getLocaleFormat, allSettled } from '../../Utils/utils.js';
import NewTradeInContext from '../../../../NewTradeInContext.js';
import ActionButton from '../../Components/ActionButton/ActionButton.jsx';
import ConfirmationMessage from '../../Components/ConfirmationMessage/ConfirmationMessage.jsx';

const ContactInformations = ({
    tradeInVehicle,
    onPreviousStep,
    onNextStep,
    onTradeInVehicleUpdated,
    onFormSubmit,
    inspectionNeeded,
    errorInspection: { questionError, answerError },
    closeModal,
    quote,
}) => {
    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [optin, setOptin] = useState(false);
    const [loading, setLoading] = useState(false);
    const [leadCreated, setLeadCreated] = useState(false);
    const [error, setError] = useState('');
    const { fieldsLocked } = useContext(NewTradeInContext);

    // On mount
    useEffect(() => {
        const fetchProfile = async () => {
            const account = await ProfileService.fetchProfile();
            if (account) {
                account?.firstname && setFirstname(account.firstname);
                account?.lastname && setLastname(account.lastname);
                account?.username && setEmail(account.username);
                account?.mobilePhone && setPhone(account.mobilePhone);
                account?.optInEmail && setOptin(account.optInEmail);
            } else throw new Error('Account is not defined');
        };

        // Get contact data from props 'tradeInVehicle'
        if (tradeInVehicle?.contact) {
            const { contact } = tradeInVehicle;
            contact?.firstname && setFirstname(contact.firstname);
            contact?.lastname && setLastname(contact.lastname);
            contact?.email && setEmail(contact.email);
            contact?.phone && setPhone(contact.phone);
            contact?.optin && setOptin(contact.optin);
        }
        // Get info from logged account
        else if (AccountService.isAuthenticated()) {
            try {
                fetchProfile();
            } catch (e) {
                this.logger.error('Error fetching the profile information', e);
            }
        }
    }, []);

    const isFormValid = () => {
        const validatePhone = VS.optional(VS.validatePhoneNumber);
        const validateEmail = (v) => REGEX.NOT_CONTAINS_TEST.test(v) && VS.validateEmail(v);
        return firstname.length > 0 && lastname.length > 0 && validateEmail(email) && validatePhone(String(phone));
    };

    // Store contact information in TradeInVehicle obj and send it to next screen
    const sendVehicleContactForm = async (event) => {
        event.preventDefault();
        if (fieldsLocked) return onNextStep();

        // Validate form
        if (isFormValid()) {
            let optionsList = '';
            if (tradeInVehicle?.selectedVehicleOptions) {
                optionsList = tradeInVehicle.selectedVehicleOptions.reduce((acc, option) => {
                    return `${acc}- ${option.description}\n`;
                }, '');
            }

            const options =
                tradeInVehicle?.selectedVehicleOptions && tradeInVehicle.selectedVehicleOptions.length > 0
                    ? LocaleService.t('TRADE_IN.VEHICLE_OPTIONS.LIST', { optionsList })
                    : LocaleService.t('TRADE_IN.SECTIONS_CONTENT.CONTACT_INFO.NO_OPTION');

            const tradeVehicleToken = LocaleService.t('TRADE_IN.SECTIONS_CONTENT.CONTACT_INFO.TOKEN_LEAD', {
                token: tradeInVehicle?.tradeVehicleToken,
            });

            const updatedTradeInVehicle = {
                contact: {
                    firstname,
                    lastname,
                    email,
                    phone,
                    optin,
                },
                details: options + tradeVehicleToken,
            };
            const data = { ...tradeInVehicle, ...updatedTradeInVehicle };
            setLoading(true);

            // tradeVehicleToken - Got from previous screen (From valuation API)
            // Returns a trade vehicle estimation
            ShowroomCBBService.getTradeInVehicleEstimation(tradeInVehicle.tradeVehicleToken)
                .then((estimation) => {
                    if (!inspectionNeeded) data.estimation = estimation;

                    const inspectionNeededMessage = inspectionNeeded
                        ? LocaleService.t('TRADE_IN.SECTIONS_CONTENT.INSPECTION_NEEDED.LEAD_INFO', {
                              questionError,
                              answerError,
                          })
                        : '';

                    // Get the quote details from V2.5 VDP page
                    const { quote: WebAutoCalculatorQuote } =
                        (window.WebAutoCalculator && window.WebAutoCalculator.getInfo()) || {};

                    const tradeInToSave = new TradeInModelNew({
                        id: '', // TODO: Fill this for Edit tradeIn
                        leadId: '', // TODO: Fill this for Edit tradeIn
                        tradeInVehicle: data,
                        estimation: inspectionNeeded ? '' : estimation,
                        profile: updatedTradeInVehicle.contact,
                        info: inspectionNeededMessage,
                    });

                    allSettled([
                        TradeInService.saveTradeInNew(
                            tradeInToSave,
                            !isEmpty(WebAutoCalculatorQuote) ? WebAutoCalculatorQuote : quote
                        ),
                        inspectionNeeded // if Inspected needed: no need to send an email
                            ? Promise.resolve('Inspected Needed: no email sent')
                            : EmailService.sendTradeInStandardEmail({
                                  websiteId: ConfigsService.getWebsiteId(),
                                  locale: LocaleService.getSimpleLocale(),
                                  clientName: `${data?.contact?.firstname} ${data?.contact?.lastname}`,
                                  year: data?.year,
                                  make: data?.make,
                                  model: data?.model,
                                  trim: data?.trim,
                                  vin: data?.vin,
                                  exteriorColor: data?.exteriorColor?.description,
                                  amount: getLocaleFormat(data?.estimation?.amount, LocaleService.getLocale()),
                                  toEmail: data?.contact?.email,
                                  conditionFormList: data?.formattedConditionReport,
                                  organizationUnitName: ConfigsService.getOrganizationUnitName() || '',
                                  organizationUnitPhone: ConfigsService.getOrganizationUnitPhone() || '',
                                  websiteDomain: ConfigsService.getWebsiteDomain() || '',
                                  websiteLogo: ConfigsService.getWebsiteLogo() || '',
                                  websiteFacebookUrl: ConfigsService.getWebsiteFacebookUrl() || '',
                                  websiteInstagramUrl: ConfigsService.getWebsiteInstagramUrl() || '',
                                  websiteTwitterUrl: ConfigsService.getWebsiteTwitterUrl() || '',
                                  websiteYoutubeUrl: ConfigsService.getWebsiteYoutubeUrl() || '',
                              }),
                    ])
                        .then(([leadResult, emailResult]) => {
                            if (inspectionNeeded) return setLeadCreated(true);
                            return onFormSubmit(data);
                        })
                        .catch(() => setError(LocaleService.t('TRADE_IN.ERROR_FETCHING')))
                        .finally(() => setLoading(false));
                })
                .catch((e) => {
                    setError(LocaleService.t('TRADE_IN.ERROR_FETCHING_ESTIMATION'));
                    setLoading(false);
                    return Promise.reject(e);
                });
        }
        return false;
    };

    return (
        <div className="contact-info-form">
            {!isEmpty(error) && (
                // Display an error box if lead creation or appraisal value request failed
                <div className="API-error-msg">
                    <Card color="alert" outline>
                        <CardSection>
                            <icon className="icon-Warning" />
                            <p>{error}</p>
                        </CardSection>
                    </Card>
                </div>
            )}
            {inspectionNeeded ? (
                <div className="contact-info-form--inspection-needed">
                    {leadCreated ? (
                        <ConfirmationMessage
                            title={LocaleService.t('TRADE_IN.THANK_YOU')}
                            description={LocaleService.t('TRADE_IN.SECTIONS_CONTENT.INSPECTION_NEEDED.CONFIRMATION')}
                            closeText={LocaleService.t('TRADE_IN.BUTTONS.KEEP_BROWSING')}
                            onClose={closeModal}
                        />
                    ) : (
                        <>
                            <div className="contact-info-form--inspection-needed-header">
                                <icon className="icon-cbb-icons-inspection" />
                                <h3 className="contact-info-form--inspection-needed-header--title">
                                    {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.INSPECTION_NEEDED.TITLE')}
                                </h3>
                            </div>
                            <p className="contact-info-form--inspection-needed--desc">
                                {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.INSPECTION_NEEDED.DESCRIPTION')}
                            </p>
                        </>
                    )}
                </div>
            ) : (
                <h5>{LocaleService.t('TRADE_IN.SECTIONS.CONTACT_INFO')}</h5>
            )}
            <div className="contact-info-form-body">
                {!leadCreated && (
                    <>
                        <p className="sr-text">{LocaleService.t('TRADE_IN.REQUIRED_FIELDS')}</p>
                        <form className="trade-in-form" onSubmit={sendVehicleContactForm}>
                            <section className="clearfix contact-info-form-body--text-input">
                                <div className="clearfix">
                                    <Input
                                        id="firstname"
                                        name="firstname"
                                        value={firstname}
                                        onChange={setFirstname}
                                        label={LocaleService.t('TRADE_IN.CONTACT.FIRST_NAME')}
                                        required
                                        disabled={fieldsLocked}
                                    />
                                </div>
                                <div className="clearfix">
                                    <Input
                                        id="lastname"
                                        name="lastname"
                                        label={LocaleService.t('TRADE_IN.CONTACT.LAST_NAME')}
                                        value={lastname}
                                        onChange={setLastname}
                                        required
                                        disabled={fieldsLocked}
                                    />
                                </div>
                            </section>
                            <section className="clearfix contact-info-form-body--text-input">
                                <div className="clearfix">
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={email}
                                        onChange={setEmail}
                                        label={LocaleService.t('TRADE_IN.CONTACT.EMAIL')}
                                        required
                                        disabled={fieldsLocked}
                                        customValidation={REGEX.NOT_CONTAINS_TEST}
                                        customMessage={LocaleService.t(
                                            'TRADE_IN.SECTIONS_CONTENT.CONTACT_INFO.NO_EMAIL_TEST'
                                        )}
                                    />
                                    {!inspectionNeeded && (
                                        <p>
                                            {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.CONTACT_INFO.SEND_EMAIL_COPY')}
                                        </p>
                                    )}
                                </div>
                                <div className="clearfix">
                                    <InputPhone
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        label={LocaleService.t('TRADE_IN.CONTACT.PHONE_NUMBER')}
                                        value={phone}
                                        onChange={(v) => setPhone(v ? v.toString() : '')}
                                        disabled={fieldsLocked}
                                    />
                                </div>
                            </section>
                            <section className="clearfix contact-info-form-body--checkbox-input">
                                <Checkbox
                                    checked={optin}
                                    onChange={setOptin}
                                    label={LocaleService.t('TRADE_IN.SECTIONS_CONTENT.CONTACT_INFO.OPTIN')}
                                    disabled={fieldsLocked}
                                />
                            </section>
                            <section
                                className="clearfix vehicle-buttons-container"
                                data-test="trade-in.vehicle-form.submit"
                            >
                                <div className="vehicle-buttons">
                                    {!inspectionNeeded && (
                                        <ActionButton onClick={() => onPreviousStep()}>
                                            {LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_CONDITION')}
                                        </ActionButton>
                                    )}
                                    <ActionButton next noPrefix loading={loading}>
                                        {inspectionNeeded
                                            ? LocaleService.t('TRADE_IN.BUTTONS.SUBMIT')
                                            : LocaleService.t('TRADE_IN.CTA.GET_APPRAISAL')}
                                    </ActionButton>
                                </div>
                            </section>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

ContactInformations.propTypes = {
    tradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel).isRequired,
};

ContactInformations.displayName = 'ContactInformations';

export default ContactInformations;
